import { useState, useRef, useCallback } from 'react';
import { uploadFile } from 'zite-file-upload-sdk';
import { transcribeVoiceNote, saveAssessmentEvidence } from 'zite-endpoints-sdk';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Mic, Square, Loader2, CheckCircle, AlertTriangle, Upload, X, Play, Pause } from 'lucide-react';
import { toast } from 'sonner';

type VoiceState = 'idle' | 'recording' | 'recorded' | 'uploading' | 'transcribing' | 'transcription_failed' | 'reviewing' | 'saving';

interface VoiceRecorderProps {
  sessionId: string;
  criterionIndex: number;
  onSaved: () => void;
  onCancel: () => void;
}

export default function VoiceRecorder({ sessionId, criterionIndex, onSaved, onCancel }: VoiceRecorderProps) {
  const [state, setState] = useState<VoiceState>('idle');
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState('');
  const [uploadedUrl, setUploadedUrl] = useState('');
  const [transcript, setTranscript] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number>(0);
  const audioPlayerRef = useRef<HTMLAudioElement | null>(null);
  const busyRef = useRef(false); // Prevent duplicate taps

  const startRecording = useCallback(async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' :
                       MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4' : 'audio/webm';
      const recorder = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: mimeType });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        setState('recorded');
        window.clearInterval(timerRef.current);
      };

      recorder.start(250);
      mediaRecorderRef.current = recorder;
      setState('recording');
      setRecordingTime(0);

      timerRef.current = window.setInterval(() => {
        setRecordingTime(t => {
          const next = t + 1;
          // Auto-stop at 120 seconds
          if (next >= 120 && mediaRecorderRef.current?.state === 'recording') {
            mediaRecorderRef.current.stop();
          }
          return next;
        });
      }, 1000);
    } catch (err: any) {
      toast.error('Microphone access denied. Please allow microphone access in your browser settings.');
      setState('idle');
    } finally {
      busyRef.current = false;
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
  }, []);

  const handleUploadFile = useCallback(async (file: File) => {
    if (busyRef.current) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error('Audio file must be under 25MB.');
      return;
    }
    busyRef.current = true;
    setAudioBlob(file);
    setAudioUrl(URL.createObjectURL(file));
    setState('recorded');
    busyRef.current = false;
  }, []);

  const uploadAndTranscribe = useCallback(async () => {
    if (!audioBlob || busyRef.current) return;
    busyRef.current = true;
    setErrorMsg('');

    try {
      // Step 1: Upload
      setState('uploading');
      const ext = audioBlob.type.includes('mp4') ? 'mp4' : 'webm';
      const { fileUrl } = await uploadFile({
        data: audioBlob,
        filename: `voice-${Date.now()}.${ext}`,
      });
      setUploadedUrl(fileUrl);

      // Step 2: Transcribe
      setState('transcribing');
      const result = await transcribeVoiceNote({
        sessionId,
        criterionIndex,
        audioFileUrl: fileUrl,
      });

      setTranscript(result.transcriptText);
      setState('reviewing');
    } catch (err: any) {
      setErrorMsg(err.message || 'Transcription failed');
      setState('transcription_failed');
    } finally {
      busyRef.current = false;
    }
  }, [audioBlob, sessionId, criterionIndex]);

  const confirmAndSave = useCallback(async () => {
    if (!transcript.trim() || busyRef.current) return;
    busyRef.current = true;
    setState('saving');

    try {
      await saveAssessmentEvidence({
        sessionId,
        criterionIndex,
        type: 'Voice',
        voiceOriginalUrl: uploadedUrl,
        transcriptText: transcript.trim(),
      });
      toast.success('Voice note saved');
      onSaved();
    } catch (err: any) {
      toast.error(err.message || 'Failed to save');
      setState('reviewing');
    } finally {
      busyRef.current = false;
    }
  }, [transcript, uploadedUrl, sessionId, criterionIndex, onSaved]);

  const reset = () => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioBlob(null);
    setAudioUrl('');
    setUploadedUrl('');
    setTranscript('');
    setErrorMsg('');
    setState('idle');
    busyRef.current = false;
  };

  const togglePlay = () => {
    if (!audioPlayerRef.current) return;
    if (isPlaying) {
      audioPlayerRef.current.pause();
    } else {
      audioPlayerRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const formatTime = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  const fileInputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="space-y-2.5">
      {/* Hidden audio element for playback */}
      {audioUrl && (
        <audio
          ref={audioPlayerRef}
          src={audioUrl}
          onEnded={() => setIsPlaying(false)}
          className="hidden"
        />
      )}

      {/* IDLE — Record or Upload */}
      {state === 'idle' && (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={startRecording}
              className="flex flex-col items-center justify-center gap-1.5 p-4 bg-destructive/10 border-2 border-destructive/20 rounded-xl text-xs font-medium text-destructive touch-manipulation active:scale-[0.98] transition-transform"
            >
              <Mic className="w-5 h-5" />
              Record
            </button>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="flex flex-col items-center justify-center gap-1.5 p-4 bg-background border border-border rounded-xl text-xs font-medium text-foreground touch-manipulation active:scale-[0.98] transition-transform"
            >
              <Upload className="w-5 h-5" />
              Upload
            </button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="audio/*"
            className="hidden"
            onChange={e => {
              const f = e.target.files?.[0];
              if (f) handleUploadFile(f);
            }}
          />
          <button onClick={onCancel} className="text-xs text-muted-foreground hover:text-foreground">Cancel</button>
        </div>
      )}

      {/* RECORDING */}
      {state === 'recording' && (
        <div className="text-center space-y-3">
          <div className="flex items-center justify-center gap-2">
            <div className="w-3 h-3 rounded-full bg-destructive animate-pulse" />
            <span className="text-sm font-semibold text-destructive">Recording {formatTime(recordingTime)}</span>
          </div>
          <p className="text-[10px] text-muted-foreground">Max 2 minutes</p>
          <Button onClick={stopRecording} variant="destructive" className="h-11 px-6 touch-manipulation">
            <Square className="w-4 h-4 mr-1.5" /> Stop
          </Button>
        </div>
      )}

      {/* RECORDED — Preview + Send */}
      {state === 'recorded' && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 p-2.5 bg-muted/50 rounded-xl">
            <button onClick={togglePlay} className="w-8 h-8 rounded-full bg-primary flex items-center justify-center shrink-0 touch-manipulation">
              {isPlaying ? <Pause className="w-3.5 h-3.5 text-primary-foreground" /> : <Play className="w-3.5 h-3.5 text-primary-foreground ml-0.5" />}
            </button>
            <div className="flex-1">
              <p className="text-xs font-medium text-foreground">Voice recording</p>
              <p className="text-[10px] text-muted-foreground">{formatTime(recordingTime)}</p>
            </div>
            <button onClick={reset} className="text-muted-foreground hover:text-foreground">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="flex gap-2">
            <Button onClick={uploadAndTranscribe} className="flex-1 h-10 text-sm touch-manipulation">
              <Mic className="w-3.5 h-3.5 mr-1" /> Transcribe
            </Button>
            <Button variant="outline" onClick={reset} className="h-10 text-sm touch-manipulation">
              Redo
            </Button>
          </div>
        </div>
      )}

      {/* UPLOADING */}
      {state === 'uploading' && (
        <div className="flex items-center gap-2 p-3 bg-muted/30 rounded-xl">
          <Loader2 className="w-4 h-4 animate-spin text-primary" />
          <span className="text-sm text-muted-foreground">Uploading audio…</span>
        </div>
      )}

      {/* TRANSCRIBING */}
      {state === 'transcribing' && (
        <div className="flex items-center gap-2 p-3 bg-muted/30 rounded-xl">
          <Loader2 className="w-4 h-4 animate-spin text-primary" />
          <span className="text-sm text-muted-foreground">Transcribing with Whisper…</span>
        </div>
      )}

      {/* TRANSCRIPTION FAILED */}
      {state === 'transcription_failed' && (
        <div className="space-y-2">
          <div className="flex items-start gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-xl">
            <AlertTriangle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-destructive">Transcription failed</p>
              <p className="text-xs text-destructive/80 mt-0.5">{errorMsg}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={uploadAndTranscribe} variant="outline" className="flex-1 h-10 text-sm touch-manipulation">
              Retry
            </Button>
            <Button variant="outline" onClick={reset} className="h-10 text-sm touch-manipulation">
              Re-record
            </Button>
          </div>
        </div>
      )}

      {/* REVIEWING — Editable transcript */}
      {state === 'reviewing' && (
        <div className="space-y-2">
          <div className="flex items-center gap-1.5 mb-1">
            <CheckCircle className="w-3.5 h-3.5 text-primary" />
            <p className="text-[11px] font-semibold text-primary">Transcript ready — review and confirm</p>
          </div>
          <Textarea
            value={transcript}
            onChange={e => setTranscript(e.target.value)}
            className="text-sm min-h-[80px] resize-none"
            autoFocus
          />
          <p className="text-[10px] text-muted-foreground">Edit the transcript if needed, then confirm to save.</p>
          <div className="flex gap-2">
            <Button onClick={confirmAndSave} disabled={!transcript.trim()} className="flex-1 h-10 text-sm touch-manipulation">
              <CheckCircle className="w-3.5 h-3.5 mr-1" /> Confirm & Save
            </Button>
            <Button variant="outline" onClick={reset} className="h-10 text-sm touch-manipulation">
              Discard
            </Button>
          </div>
        </div>
      )}

      {/* SAVING */}
      {state === 'saving' && (
        <div className="flex items-center gap-2 p-3 bg-muted/30 rounded-xl">
          <Loader2 className="w-4 h-4 animate-spin text-primary" />
          <span className="text-sm text-muted-foreground">Saving voice note…</span>
        </div>
      )}
    </div>
  );
}
